/** What an entity's icon shows while background work runs on it. One selector for every surface. */
import {
  BACKGROUND_TASK_KIND,
  BACKGROUND_TASK_STATUS,
  isBackgroundTaskActive,
  mostUrgentTask,
  readBackgroundTasks,
} from '@bower/shared/constants/background-task';
import { isTerminalOcrCode, isTerminalTranscriptCode } from '@bower/shared/schemas/upload.schema';
import type { BackgroundTaskKind, BackgroundTaskStatus } from '@bower/shared';
import { CAPTURE_TASK_COPY, TASK_LABELS } from './taskCopy';

export type ActivityState = 'busy' | 'error' | 'rest';
export interface EntityActivity {
  readonly state: ActivityState;
  readonly label?: string;
}
export interface TaskSignal {
  readonly kind: BackgroundTaskKind;
  readonly status: BackgroundTaskStatus;
  /** Absent on a legacy (`task`) signal, which belongs to its kind's slot. */
  readonly slotKey?: string;
  readonly errorCode?: string;
}

/** Live /sync signals, by entity id then slot. */
export type LiveTasks = Readonly<Record<string, Readonly<Record<string, TaskSignal>>>>;

export const slotOf = (task: TaskSignal): string => task.slotKey ?? task.kind;

/**
 * The note-transcription and note-ocr failures that are a property of the
 * input, not a fault, get their own glyph label from `utils/taskCopy.ts`
 * (shared with the note's attachment tile, so the glyph and the tile never
 * disagree about why a recording or photo has no result). Not every terminal
 * code has a label: an unmapped one falls through to the kind's generic
 * error label.
 */
function errorLabel(task: TaskSignal, fallback: string): string {
  if (
    task.kind === BACKGROUND_TASK_KIND.NOTE_TRANSCRIPTION &&
    isTerminalTranscriptCode(task.errorCode)
  ) {
    return CAPTURE_TASK_COPY['note-transcription'].label.byCode[task.errorCode] ?? fallback;
  }
  if (task.kind === BACKGROUND_TASK_KIND.NOTE_OCR && isTerminalOcrCode(task.errorCode)) {
    return CAPTURE_TASK_COPY['note-ocr'].label.byCode[task.errorCode] ?? fallback;
  }
  return fallback;
}

export function activityFromTask(task: TaskSignal | null | undefined): EntityActivity {
  if (!task) return { state: 'rest' };
  // A kind the backend shipped ahead of this frontend build has no label
  // yet: read as rest rather than indexing TASK_LABELS with an unmapped key.
  const label = TASK_LABELS[task.kind] as { busy: string; error: string } | undefined;
  if (!label) return { state: 'rest' };
  if (isBackgroundTaskActive(task)) return { state: 'busy', label: label.busy };
  if (task.status === BACKGROUND_TASK_STATUS.FAILED) {
    return { state: 'error', label: errorLabel(task, label.error) };
  }
  return { state: 'rest' };
}

/** The most urgent known task decides; an unmapped kind is ignored, not ranked. */
export function activityFromTasks(tasks: readonly TaskSignal[]): EntityActivity {
  return activityFromTask(mostUrgentTask(tasks.filter((t) => t.kind in TASK_LABELS)));
}

const KNOWN_KINDS = new Set<string>(Object.values(BACKGROUND_TASK_KIND));
const KNOWN_STATUSES = new Set<string>(Object.values(BACKGROUND_TASK_STATUS));

/** Validates a signal at the socket boundary before it reaches the store. */
export function isValidTaskSignal(value: unknown): value is TaskSignal {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.kind === 'string' &&
    KNOWN_KINDS.has(v.kind) &&
    typeof v.status === 'string' &&
    KNOWN_STATUSES.has(v.status) &&
    (v.slotKey === undefined || typeof v.slotKey === 'string') &&
    (v.errorCode === undefined || typeof v.errorCode === 'string')
  );
}

/**
 * Rows with each live signal written into its slot, so pure presentation code
 * reading `processingData` sees an event before the refetch lands. A row with
 * no signal is returned as the same object, and when no row has one the input
 * array itself is returned (memo-friendly: a signal for an unrelated entity
 * leaves the list's identity alone).
 */
export function overlayLiveTasks<
  T extends { id: string; processingData?: Record<string, unknown> },
>(rows: T[], live: LiveTasks): T[] {
  if (!rows.some((row) => live[row.id])) return rows;
  return rows.map((row) => {
    const signals = live[row.id];
    if (!signals) return row;
    const current = readBackgroundTasks(row.processingData);
    const tasks: Record<string, unknown> = {
      ...((row.processingData?.tasks as Record<string, unknown> | undefined) ?? {}),
    };
    for (const [slotKey, signal] of Object.entries(signals)) {
      tasks[slotKey] = {
        kind: signal.kind,
        status: signal.status,
        requestedAt: current[slotKey]?.requestedAt ?? '',
        updatedAt: current[slotKey]?.updatedAt ?? '',
        ...(signal.errorCode ? { errorCode: signal.errorCode } : {}),
      };
    }
    return { ...row, processingData: { ...row.processingData, tasks } };
  });
}
